import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  clientsService,
  type FetchClientsParams,
  type Industry,
} from '../services/clients.service';
import type { Client, ClientsResponse } from '@lnr-v2/types';

/**
 * Options for the useClients hook
 */
export interface UseClientsOptions {
  /** Filter by tags - single tag string (e.g., "Travel") */
  tags?: string;
  /** Search query string */
  search?: string;
  /** Filter by active status */
  isActive?: boolean;
  /** Filter by featured status */
  isFeatured?: boolean;
  /** Page number (1-indexed) */
  page?: number;
  /** Items per page */
  limit?: number;
  /** Sort field */
  sortBy?: 'name' | 'createdAt' | 'updatedAt';
  /** Sort direction */
  sortOrder?: 'ASC' | 'DESC';
  /** Whether to enable the query (default: true) */
  enabled?: boolean;
}

/**
 * Return type for the useClients hook
 */
export interface UseClientsReturn {
  /** Array of client items */
  clients: Client[];
  /** Pagination metadata */
  pagination: ClientsResponse['pagination'] | null;
  /** Loading state (true only on initial load) */
  loading: boolean;
  /** Error message if request failed */
  error: string | null;
  /** Whether the query is currently refetching in the background */
  isRefetching: boolean;
  /** Function to manually trigger a refetch */
  refetch: () => void;
  /** Whether there are more pages available */
  hasNextPage: boolean;
  /** Total number of items across all pages */
  total: number;
}

/**
 * React Query hook for fetching clients with proper caching and state management
 *
 * This hook replaces the previous useState-based approach with React Query,
 * providing automatic caching, request deduplication, and background refetching.
 *
 * @param options - Query options for filtering, sorting, and pagination
 * @returns Object containing clients data, loading state, and helper functions
 *
 * @example
 * ```tsx
 * const { clients, loading, error } = useClients({
 *   tags: 'Travel',
 *   page: 1,
 *   limit: 20
 * });
 * ```
 */
export const useClients = (
  options: UseClientsOptions = {}
): UseClientsReturn => {
  const {
    tags,
    search,
    isActive,
    isFeatured,
    page = 1,
    limit = 20,
    sortBy,
    sortOrder,
    enabled = true,
  } = options;

  // Build query parameters
  const params: FetchClientsParams = {
    tags,
    search,
    isActive,
    isFeatured,
    page,
    limit,
    sortBy,
    sortOrder,
  };

  // Use React Query for data fetching with proper cache key
  const query: UseQueryResult<ClientsResponse, Error> = useQuery({
    queryKey: ['clients', params],
    queryFn: () => clientsService.fetchClients(params),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Extract data with safe defaults
  const clients = query.data?.items ?? [];
  const pagination = query.data?.pagination ?? null;
  const errorMessage = query.error?.message ?? null;

  return {
    clients,
    pagination,
    loading: query.isLoading,
    error: errorMessage,
    isRefetching: query.isRefetching,
    refetch: () => {
      void query.refetch();
    },
    hasNextPage: pagination?.hasNext ?? false,
    total: pagination?.total ?? 0,
  };
};

/**
 * Return type for useIndustries hook
 */
export interface UseIndustriesReturn {
  /** Array of industry options (includes "All" as first option) */
  industries: Industry[];
  /** Loading state */
  loading: boolean;
  /** Error message if request failed */
  error: string | null;
}

/**
 * Hook for getting available industries for filtering
 *
 * Fetches industries dynamically from the Industries taxonomy API.
 * Returns an array with "All" as the first option followed by available industries.
 *
 * @returns Object containing industries array, loading state, and error
 *
 * @example
 * ```tsx
 * const { industries, loading, error } = useIndustries();
 * // industries: [{ id: 'all', name: 'All', slug: 'all' }, { name: 'Travel', ... }, ...]
 * ```
 */
export const useIndustries = (): UseIndustriesReturn => {
  const query = useQuery({
    queryKey: ['industries'],
    queryFn: () => clientsService.fetchIndustries(),
    staleTime: 10 * 60 * 1000, // 10 minutes - industries don't change often
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  // Prepend "All" option to the industries list
  const allOption = { id: 'all', name: 'All', slug: 'all' };
  const industries = [allOption, ...(query.data ?? [])];

  return {
    industries,
    loading: query.isLoading,
    error: query.error?.message ?? null,
  };
};

/**
 * @deprecated Use useIndustries instead
 * Hook for getting available client tags (legacy)
 */
export const useClientTags = (): string[] => {
  const { industries } = useIndustries();
  return industries.map((i) => i.name);
};

export default useClients;
